import { useState, useEffect, useRef } from 'react';
import { LogoIcon } from './Logo';
import { useLanguage } from './LanguageContext';

// ─── مفتاح موياسر (ضع مفتاحك الحقيقي هنا) ───────────────
const MOYASAR_KEY = process.env.REACT_APP_MOYASAR_KEY;

// ─── بيانات الباقات ───────────────────────────────────────
const PLANS_INFO = {
  basic: { nameKey: 'payment_planBasic', icon: '⭐', color: '#3b82f6' },
  pro:   { nameKey: 'payment_planPro',   icon: '🚀', color: '#7c6df8' },
  elite: { nameKey: 'payment_planElite', icon: '🌍', color: '#f59e0b' },
};

export default function PaymentScreen({ plan, user, onSuccess, onBack, onHome }) {
  const { t, isRTL } = useLanguage();
  const [status, setStatus]   = useState('idle'); // idle | loading | success | error
  const [errMsg, setErrMsg]   = useState('');
  const formRef               = useRef(null);

  // مستخدم مسجّل فعلياً؟ بدون id لا يمكن ربط الدفعة بأحد.
  const isAuthed = !!user?.id && !user?.isGuest;

  const planInfoRaw = PLANS_INFO[plan.id] || {};
  const planInfo    = { ...planInfoRaw, name: t(planInfoRaw.nameKey) };
  const amountSar   = plan.price;
  const pageStyle   = { ...styles.page, direction: isRTL ? 'rtl' : 'ltr' };

  // ── تحميل سكريبت موياسر ──────────────────────────────
  useEffect(() => {
    // لا نحمّل نموذج الدفع إطلاقاً لمستخدم غير مسجّل
    if (!isAuthed) return;

    // CSS override للثيم الداكن
    if (!document.getElementById('moyasar-dark-style')) {
      const style = document.createElement('style');
      style.id = 'moyasar-dark-style';
      style.innerHTML = `
        .mysr-form { background: transparent !important; }
        .mysr-form input, .mysr-form select {
          background: #1e293b !important; color: #fff !important;
          border: 1.5px solid #334155 !important; border-radius: 8px !important;
        }
        .mysr-form label { color: #94a3b8 !important; }
        .mysr-form-btn {
          background: linear-gradient(135deg,#7c3aed,#a855f7) !important;
          border: none !important; color: #fff !important;
          border-radius: 10px !important; font-weight: 700 !important;
        }
      `;
      document.head.appendChild(style);
    }

    // إذا موياسر محملة مسبقاً
    if (window.Moyasar) {
      setTimeout(initMoyasar, 100);
      return;
    }

    // CSS موياسر
    if (!document.querySelector('link[href*="moyasar"]')) {
      const link = document.createElement('link');
      link.rel  = 'stylesheet';
      link.href = 'https://cdn.moyasar.com/mpf/1.15.0/moyasar.css';
      document.head.appendChild(link);
    }

    // JS موياسر
    if (!document.querySelector('script[src*="moyasar"]')) {
      const script = document.createElement('script');
      script.src   = 'https://cdn.moyasar.com/mpf/1.15.0/moyasar.js';
      script.async = true;
      script.onload = () => setTimeout(initMoyasar, 200);
      document.head.appendChild(script);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  // ── تفعيل الاشتراك بعد الدفع ─────────────────────────
  //  السيرفر يتحقق من موياسر، يطابق المبلغ بجدول الباقات،
  //  يسجّل الدفعة، وينشئ الاشتراك. المتصفح لا يكتب أي شيء.
  const handlePaymentSuccess = async (moyasarPaymentId) => {
    setStatus('loading');
    try {
      const verifyRes = await fetch('/api/verify-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentId: moyasarPaymentId,
          // نمرّرهما كشبكة أمان لو ضاعت الـ metadata من موياسر
          userId: user?.id || null,
          phone:  user?.phone || null,
        }),
      });
      const verifyData = await verifyRes.json();

      if (!verifyData.ok) {
        setErrMsg('تعذّر تفعيل الاشتراك. تواصل مع الدعم على support@qadder.sa مع ذكر رقم العملية: ' + moyasarPaymentId);
        setStatus('error');
        return;
      }

      localStorage.removeItem('qadder_pending_payment');
      setStatus('success');
      setTimeout(() => onSuccess(plan), 2000);
    } catch {
      setErrMsg('تم الدفع لكن تعذّر تأكيد التفعيل. تواصل مع الدعم على support@qadder.sa مع ذكر رقم العملية: ' + moyasarPaymentId);
      setStatus('error');
    }
  };

  // ── تهيئة نموذج موياسر ───────────────────────────────
  const initMoyasar = () => {
    // حارس أخير: لا نهيّئ نموذج دفع بدون مستخدم مسجّل
    if (!user?.id) return;
    if (!window.Moyasar) { setTimeout(initMoyasar, 300); return; }
    if (!formRef.current) { setTimeout(initMoyasar, 200); return; }
    // امسح أي فورم سابق
    formRef.current.innerHTML = '';

    // حفظ بيانات الدفع المعلق — تُستخدم عند عودة المستخدم بعد 3DS
    localStorage.setItem('qadder_pending_payment', JSON.stringify({
      userId: user.id,
      planId: plan.id,
      amountSar,
    }));

    const paymentMetadata = {
      user_id:    user.id,
      plan_id:    plan.id,
      user_phone: user.phone || '',
      amount_sar: String(amountSar),
    };

    const isApplePayDomain = window.location.hostname === 'www.qadder.sa';
    const supportsApplePay = isApplePayDomain && !!(window.ApplePaySession?.canMakePayments?.());
    const samsungServiceId = process.env.REACT_APP_SAMSUNG_SERVICE_ID;

    const buildConfig = (withApplePay) => {
      const methods = ['creditcard'];
      if (withApplePay) methods.push('applepay');
      if (samsungServiceId) methods.push('samsungpay');
      return {
        element:             '.mysr-form',
        amount:              amountSar * 100,
        currency:            'SAR',
        description:         `اشتراك قادر — باقة ${planInfo.name}`,
        publishable_api_key: MOYASAR_KEY,
        methods,
        supported_networks:  ['visa', 'mastercard', 'mada'],
        callback_url:        window.location.origin + '/',
        metadata:            paymentMetadata,
        ...(withApplePay ? {
          apple_pay: {
            country:               'SA',
            label:                 'Qadder Subscription',
            validate_merchant_url: 'https://api.moyasar.com/v1/applepay/initiate',
          },
        } : {}),
        ...(samsungServiceId ? {
          samsung_pay: {
            service_id:   samsungServiceId,
            order_number: `qadder-${Date.now()}`,
            country:      'SA',
            label:        'Qadder Subscription',
            environment:  'PRODUCTION',
          },
        } : {}),
        on_completed: async (payment) => { await handlePaymentSuccess(payment.id); },
        on_failure:   (error) => {
          setErrMsg(error?.message || 'فشلت عملية الدفع، حاول مجدداً');
          setStatus('error');
        },
      };
    };

    try {
      window.Moyasar.init(buildConfig(supportsApplePay));
    } catch {
      // إذا فشل Apple Pay (دومين غير مسجّل أو جهاز غير مدعوم) — نعيد بدونه
      formRef.current.innerHTML = '';
      window.Moyasar.init(buildConfig(false));
    }
  };

  // ── يجب تسجيل الدخول قبل الدفع ───────────────────────
  //  بدون user.id تذهب الدفعة لموياسر بلا هوية ويستحيل تفعيلها آلياً.
  if (!isAuthed) return (
    <div style={pageStyle}>
      <div style={{ ...styles.card, textAlign: 'center' }}>
        <div style={{ fontSize: 60, marginBottom: 16 }}>🔐</div>
        <h2 style={{ color: 'var(--text)', fontSize: 20, fontWeight: 900, margin: '0 0 10px' }}>
          سجّل دخولك قبل الدفع
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.9, margin: '0 0 24px' }}>
          لازم نعرف الحساب اللي بينضاف له الاشتراك. سجّل دخولك برقم جوالك،
          وبعدها ارجع لهذي الصفحة وأكمل الدفع.
        </p>
        <button onClick={onHome || onBack} style={styles.btnPrimary}>
          تسجيل الدخول
        </button>
        <button onClick={onBack} style={{ ...styles.btnSecondary, marginTop: 10 }}>
          {t('payment_backBtn')}
        </button>
      </div>
    </div>
  );

  // ── شاشة النجاح ──────────────────────────────────────
  if (status === 'success') return (
    <div style={pageStyle}>
      <div style={styles.card}>
        <div style={{ fontSize: 72, marginBottom: 16 }}>🎉</div>
        <h2 style={{ color: '#86efac', fontSize: 24, fontWeight: 900, margin: '0 0 8px' }}>
          {t('payment_successTitle')}
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: '0 0 24px' }}>
          {t('payment_welcome') || `${planInfo.icon} ${planInfo.name}`}
        </p>
        <div style={{ ...styles.badge, background: planInfo.color + '22', borderColor: planInfo.color + '44' }}>
          <span style={{ color: planInfo.color, fontWeight: 700 }}>
            {planInfo.icon} {planInfo.name} — {t('payment_duration')}
          </span>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 16 }}>
          {t('payment_activating')}
        </p>
      </div>
    </div>
  );

  // ── شاشة الخطأ ───────────────────────────────────────
  if (status === 'error') return (
    <div style={pageStyle}>
      <div style={styles.card}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>❌</div>
        <h2 style={{ color: '#fca5a5', fontSize: 22, fontWeight: 900, margin: '0 0 8px' }}>
          {t('payment_errorTitle')}
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 24px' }}>{errMsg}</p>
        <button onClick={() => { setStatus('idle'); setErrMsg(''); }} style={styles.btnPrimary}>
          {t('payment_retry')}
        </button>
        <button onClick={onBack} style={{ ...styles.btnSecondary, marginTop: 10 }}>
          {t('payment_backBtn')}
        </button>
      </div>
    </div>
  );

  // ── شاشة الدفع الرئيسية ──────────────────────────────
  return (
    <div style={pageStyle}>

      {/* Header */}
      <div style={{ width: '100%', maxWidth: 480, marginBottom: 20 }}>
        <button onClick={onBack} style={styles.backBtn}>{t('payment_backArrow')}</button>
      </div>

      {/* ملخص الطلب */}
      <div style={{ ...styles.card, marginBottom: 16 }}>
        <button onClick={onHome || onBack} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'Tajawal, sans-serif', textAlign: 'right' }}>
          <LogoIcon size={44} />
          <div>
            <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 900, margin: 0 }}>{t('appName')}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 12, margin: 0 }}>{t('appSlogan')}</p>
          </div>
        </button>

        {/* تفاصيل الباقة */}
        <div style={{
          background: planInfo.color + '11',
          border: `1px solid ${planInfo.color}33`,
          borderRadius: 14,
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 16 }}>
              {planInfo.icon} {planInfo.name}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
              {t('payment_monthly')}
            </div>
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ color: planInfo.color, fontWeight: 900, fontSize: 28 }}>
              {amountSar}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{t('perMonth')}</div>
          </div>
        </div>

        {/* معلومات المشترك */}
        <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 10 }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>📱 {user?.phone}</span>
        </div>
      </div>

      {/* نموذج موياسر */}
      <div style={{ ...styles.card, padding: 24 }}>
        <h3 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700, margin: '0 0 16px', textAlign: isRTL ? 'right' : 'left' }}>
          {t('payment_formTitle')}
        </h3>

        {/* موياسر يحقن النموذج هنا */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '16px 12px',
          minHeight: 200,
        }}>
          <div className="mysr-form" ref={formRef} />
        </div>

        {status === 'loading' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ color: '#7c6df8', fontSize: 14 }}>{t('payment_loadingPayment')}</div>
          </div>
        )}
      </div>

      {/* ضمانات */}
      <div style={{ maxWidth: 480, marginTop: 16, textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
          {t('payment_guarantee')}
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 8 }}>
          {['visa', 'mastercard', 'mada', 'apple-pay', 'samsung-pay'].map(m => (
            <span key={m} style={{
              background: 'var(--bg-card)', border: '1px solid #334155',
              borderRadius: 6, padding: '4px 10px',
              color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600,
            }}>
              {m === 'visa' ? 'VISA' : m === 'mastercard' ? 'MC' : m === 'mada' ? 'مدى' : m === 'apple-pay' ? '🍎 Pay' : '📱 Samsung Pay'}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
          {[
            { labelKey: 'payment_privacyLink', href: '/privacy.html' },
            { labelKey: 'payment_termsLink', href: '/terms.html' },
            { labelKey: 'payment_refundLink', href: '/payment-policy.html' },
          ].map(link => (
            <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--text-muted)', fontSize: 11, textDecoration: 'none', fontFamily: 'Tajawal, sans-serif', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            >
              {t(link.labelKey)}
            </a>
          ))}
        </div>
        <div style={{ marginTop: 12, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <a href="mailto:support@qadder.sa"
            style={{ color: 'var(--text-muted)', fontSize: 11, textDecoration: 'none', fontFamily: 'Tajawal, sans-serif' }}>
            📧 support@qadder.sa
          </a>
          <a href="https://t.me/qadder_sa" target="_blank" rel="noreferrer"
            style={{ color: '#229ED9', fontSize: 11, textDecoration: 'none', fontFamily: 'Tajawal, sans-serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="15" height="15"><circle cx="24" cy="24" r="24" fill="#229ED9"/><path fill="#fff" d="M35.2 14.3 30.9 34c-.3 1.4-1.2 1.8-2.4 1.1l-6.6-4.9-3.2 3.1c-.4.4-.6.6-1.2.6l.4-6.7 12.2-11c.5-.5-.1-.7-.8-.3L14.4 26.1l-6.6-2.1c-1.4-.4-1.5-1.4.3-2.1l25.9-10c1.2-.4 2.2.3 1.2 2.4z"/></svg>
            تيليقرام
          </a>
          <a href="https://x.com/qaddersa" target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--text-muted)', fontSize: 11, textDecoration: 'none', fontFamily: 'Tajawal, sans-serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            @qaddersa
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── الأنماط ──────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontFamily: 'Tajawal, sans-serif',
    direction: 'rtl',
    padding: '24px 16px 40px',
  },
  card: {
    width: '100%',
    maxWidth: 480,
    background: 'var(--bg-card)',
    borderRadius: 20,
    border: '1px solid #334155',
    padding: '24px 20px',
  },
  badge: {
    display: 'inline-block',
    padding: '8px 20px',
    borderRadius: 50,
    border: '1px solid',
    fontSize: 14,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    fontSize: 16,
    cursor: 'pointer',
    fontFamily: 'Tajawal, sans-serif',
    padding: 0,
  },
  btnPrimary: {
    width: '100%',
    background: 'linear-gradient(135deg,#7c6df8,#a78bfa)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Tajawal, sans-serif',
  },
  btnSecondary: {
    width: '100%',
    background: 'none',
    color: 'var(--text-secondary)',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 12,
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'Tajawal, sans-serif',
  },
};
